import { marketplaceService } from '../services/marketplace';
import type { OperatorBankAccount, OperatorProfile, OperatorTrailer, OperatorVehicle, TrailerPhoto, VehiclePhoto } from '../types/marketplace';
import { normalizeCollection, toErrorMessage, type MarketplaceGet, type MarketplaceSet } from './marketplaceStoreTypes';

function sortVehiclePhotos(photos: VehiclePhoto[]): VehiclePhoto[] {
    return [...photos].sort((left, right) => {
        if (left.display_order !== right.display_order) {
            return left.display_order - right.display_order;
        }

        return left.id - right.id;
    });
}

function sortTrailerPhotos(photos: TrailerPhoto[]): TrailerPhoto[] {
    return [...photos].sort((left, right) => {
        if (left.display_order !== right.display_order) {
            return left.display_order - right.display_order;
        }

        return left.id - right.id;
    });
}

export function createOperatorSlice(set: MarketplaceSet, get: MarketplaceGet) {
    return {
        operatorProfile: null as OperatorProfile | null,
        operatorBankAccounts: [] as OperatorBankAccount[],
        operatorVehicles: [] as OperatorVehicle[],
        operatorTrailers: [] as OperatorTrailer[],
        canOperatorQuote: false,
        operatorQuoteReason: null as string | null,
        operatorProfileLoaded: false,
        isLoadingOperator: false,
        isSavingOperator: false,
        isLoadingTrailers: false,

        fetchOperatorProfile: async (): Promise<OperatorProfile | null> => {
            set({ isLoadingOperator: true, operatorProfileLoaded: false, error: null });

            try {
                const response = await marketplaceService.getOperatorProfile();
                const profile = response.data.profile;

                set({
                    operatorProfile: profile,
                    operatorBankAccounts: normalizeCollection<OperatorBankAccount>(response.data.bank_accounts),
                    operatorVehicles: normalizeCollection<OperatorVehicle>(response.data.vehicles),
                    canOperatorQuote: response.data.can_quote,
                    operatorQuoteReason: response.data.can_quote_reason,
                    operatorProfileLoaded: true,
                    isLoadingOperator: false,
                });

                return profile;
            } catch (error) {
                set({
                    operatorProfileLoaded: false,
                    isLoadingOperator: false,
                    error: toErrorMessage(error, 'No se pudo cargar el perfil de operador.'),
                });
                return null;
            }
        },

        saveOperatorProfile: async (payload: Parameters<typeof marketplaceService.upsertOperatorProfile>[0]): Promise<boolean> => {
            set({ isSavingOperator: true, error: null });

            try {
                const response = await marketplaceService.upsertOperatorProfile(payload);

                set((state) => ({
                    operatorProfile: response.data,
                    canOperatorQuote: response.data.carta_porte_completo
                        && state.operatorVehicles.some((vehicle) => vehicle.active && vehicle.carta_porte_completo),
                    operatorQuoteReason: response.data.carta_porte_completo
                        ? (state.operatorVehicles.some((vehicle) => vehicle.active && vehicle.carta_porte_completo)
                            ? null
                            : 'Debes tener al menos una unidad activa con datos Carta Porte completos para cotizar.')
                        : 'Debes completar tu perfil operativo de Carta Porte para cotizar.',
                    isSavingOperator: false,
                }));

                return true;
            } catch (error) {
                set({
                    isSavingOperator: false,
                    error: toErrorMessage(error, 'No se pudo guardar el perfil de operador.'),
                });
                return false;
            }
        },

        createOperatorBankAccount: async (payload: Parameters<typeof marketplaceService.createOperatorBankAccount>[0]): Promise<boolean> => {
            set({ isSavingOperator: true, error: null });

            try {
                await marketplaceService.createOperatorBankAccount(payload);
                await get().fetchOperatorProfile();
                set({ isSavingOperator: false });
                return true;
            } catch (error) {
                set({
                    isSavingOperator: false,
                    error: toErrorMessage(error, 'No se pudo registrar la cuenta bancaria.'),
                });
                return false;
            }
        },

        updateOperatorBankAccount: async (accountId: number, payload: Parameters<typeof marketplaceService.updateOperatorBankAccount>[1]): Promise<boolean> => {
            set({ isSavingOperator: true, error: null });

            try {
                await marketplaceService.updateOperatorBankAccount(accountId, payload);
                await get().fetchOperatorProfile();
                set({ isSavingOperator: false });
                return true;
            } catch (error) {
                set({
                    isSavingOperator: false,
                    error: toErrorMessage(error, 'No se pudo actualizar la cuenta bancaria.'),
                });
                return false;
            }
        },

        deleteOperatorBankAccount: async (accountId: number): Promise<boolean> => {
            set({ isSavingOperator: true, error: null });

            try {
                await marketplaceService.deleteOperatorBankAccount(accountId);
                await get().fetchOperatorProfile();
                set({ isSavingOperator: false });
                return true;
            } catch (error) {
                set({
                    isSavingOperator: false,
                    error: toErrorMessage(error, 'No se pudo eliminar la cuenta bancaria.'),
                });
                return false;
            }
        },

        createOperatorVehicle: async (
            payload: Parameters<typeof marketplaceService.createOperatorVehicle>[0]
        ): Promise<OperatorVehicle | null> => {
            set({ isSavingOperator: true, error: null });

            try {
                const response = await marketplaceService.createOperatorVehicle(payload);
                await get().fetchOperatorProfile();
                set({ isSavingOperator: false });
                return response.data;
            } catch (error) {
                set({
                    isSavingOperator: false,
                    error: toErrorMessage(error, 'No se pudo registrar la unidad.'),
                });
                return null;
            }
        },

        updateOperatorVehicle: async (
            vehicleId: number,
            payload: Parameters<typeof marketplaceService.updateOperatorVehicle>[1]
        ): Promise<OperatorVehicle | null> => {
            set({ isSavingOperator: true, error: null });

            try {
                const response = await marketplaceService.updateOperatorVehicle(vehicleId, payload);
                await get().fetchOperatorProfile();
                set({ isSavingOperator: false });
                return response.data;
            } catch (error) {
                set({
                    isSavingOperator: false,
                    error: toErrorMessage(error, 'No se pudo actualizar la unidad.'),
                });
                return null;
            }
        },

        deleteOperatorVehicle: async (vehicleId: number): Promise<boolean> => {
            set({ isSavingOperator: true, error: null });

            try {
                await marketplaceService.deleteOperatorVehicle(vehicleId);
                await get().fetchOperatorProfile();
                set({ isSavingOperator: false });
                return true;
            } catch (error) {
                set({
                    isSavingOperator: false,
                    error: toErrorMessage(error, 'No se pudo eliminar la unidad.'),
                });
                return false;
            }
        },

        syncOperatorVehiclePhotos: (vehicleId: number, photos: VehiclePhoto[]): void => {
            const nextPhotos = sortVehiclePhotos(photos);

            set((state) => ({
                operatorVehicles: state.operatorVehicles.map((vehicle) => {
                    if (vehicle.id !== vehicleId) {
                        return vehicle;
                    }

                    return {
                        ...vehicle,
                        photos: nextPhotos,
                        photos_count: nextPhotos.length,
                        cover_url: nextPhotos[0]?.url ?? null,
                    };
                }),
            }));
        },

        fetchOperatorTrailers: async (): Promise<void> => {
            set({ isLoadingTrailers: true, error: null });

            try {
                const response = await marketplaceService.getOperatorTrailers();

                set({
                    operatorTrailers: normalizeCollection<OperatorTrailer>(response.data),
                    isLoadingTrailers: false,
                });
            } catch (error) {
                set({
                    isLoadingTrailers: false,
                    error: toErrorMessage(error, 'No se pudieron cargar los remolques.'),
                });
            }
        },

        createOperatorTrailer: async (payload: Parameters<typeof marketplaceService.createOperatorTrailer>[0]): Promise<boolean> => {
            set({ isSavingOperator: true, error: null });

            try {
                await marketplaceService.createOperatorTrailer(payload);
                await get().fetchOperatorTrailers();
                set({ isSavingOperator: false });
                return true;
            } catch (error) {
                set({
                    isSavingOperator: false,
                    error: toErrorMessage(error, 'No se pudo registrar el remolque.'),
                });
                return false;
            }
        },

        updateOperatorTrailer: async (trailerId: number, payload: Parameters<typeof marketplaceService.updateOperatorTrailer>[1]): Promise<boolean> => {
            set({ isSavingOperator: true, error: null });

            try {
                await marketplaceService.updateOperatorTrailer(trailerId, payload);
                await get().fetchOperatorTrailers();
                set({ isSavingOperator: false });
                return true;
            } catch (error) {
                set({
                    isSavingOperator: false,
                    error: toErrorMessage(error, 'No se pudo actualizar el remolque.'),
                });
                return false;
            }
        },

        deleteOperatorTrailer: async (trailerId: number): Promise<boolean> => {
            set({ isSavingOperator: true, error: null });

            try {
                await marketplaceService.deleteOperatorTrailer(trailerId);
                await get().fetchOperatorTrailers();
                set({ isSavingOperator: false });
                return true;
            } catch (error) {
                set({
                    isSavingOperator: false,
                    error: toErrorMessage(error, 'No se pudo eliminar el remolque.'),
                });
                return false;
            }
        },

        syncOperatorTrailerPhotos: (trailerId: number, photos: TrailerPhoto[]): void => {
            const nextPhotos = sortTrailerPhotos(photos);

            set((state) => ({
                operatorTrailers: state.operatorTrailers.map((trailer) => {
                    if (trailer.id !== trailerId) {
                        return trailer;
                    }

                    return {
                        ...trailer,
                        photos: nextPhotos,
                        photos_count: nextPhotos.length,
                        cover_url: nextPhotos[0]?.url ?? null,
                    };
                }),
            }));
        },
    };
}
